import { useState } from 'react'
import { useMutation } from '@hooks/graphql'
import { gql } from 'graphql-request'
import { useMutation as useTanstackMutation, useQueryClient } from '@tanstack/react-query'
import { client } from '@helpers/api'
import { useCurrentUser } from '@contexts/currentUser'

import { Button } from '@/components/ui/button'
import BasicLayout from '@/components/Onboarding/Layout/BasicLayout'
import { pluralize } from '@/helpers/format'
import { useFlashMessage } from '@/components/FlashMessage'
const REQUEST_TO_JOIN_ORGANIZATION_MUTATION = gql`
  mutation requestToJoinOrganization($input: RequestToJoinOrganizationInput!) {
    requestToJoinOrganization(input: $input) {
      organization {
        id
      }
    }
  }
`

const JoinOrganization = ({ organizations, switchToCreate }) => {
  const [requestingOrganizationId, setRequestingOrganizationId] = useState(null)
  const [requested, setRequested] = useState(false)

  const { setFlashMessage } = useFlashMessage()

  const { mutate: requestToJoinOrganization, isLoading: isRequesting } = useMutation({
    gqlMutation: REQUEST_TO_JOIN_ORGANIZATION_MUTATION,
    onSuccess: () => {
      setRequested(true)
      setRequestingOrganizationId(null)
    },
    onError: () => {
      setFlashMessage('Something went wrong', 'error')
    }
  })

  const handleJoinRequest = (organizationId) => {
    setRequestingOrganizationId(organizationId)
    requestToJoinOrganization({ input: { organizationId } })
  }

  const { clearUser } = useCurrentUser()
  const queryClient = useQueryClient()
  const { mutate: signOut } = useTanstackMutation(['signOut'], async () => client.delete('/users/sign_out'),
    {
      onSuccess: () => {
        clearUser()
        queryClient.clear()
        navigate('/sign_in')
      }
    }
  )

  const organizationInitials = (name) => {
    return name.split(' ').map(n => n[0]).slice(0,2).join('').toUpperCase()
  }

  return (
    <BasicLayout>
      <Choose>
        <When condition={!requested}>
          <h1 className='text-2xl font-extrabold tracking-tight my-1'>
            Join your organization!
          </h1>
          <p className='my-2'>
            Your email domain is associated with the following workspaces.
          </p>
          
          <div className="space-y-2 my-4">
            <For each="org" of={organizations}>
              <div 
                key={org.id} 
                className="flex items-center p-4 border rounded-lg gap-4"
              >
                <div className='rounded-full bg-accent w-10 h-10 flex items-center justify-center font-medium'>
                  {organizationInitials(org.name)}
                </div>
                <div className="flex flex-col text-sm">
                  <span className="font-semibold">{org.name}</span>
                  <span className="text-muted-foreground leading-tight">{org.membershipsCount} {pluralize('member', org.membershipsCount)}</span>
                </div>
                <div className="flex-grow"></div>
                <Button 
                  variant='outline'
                  size='sm'
                  disabled={!!requestingOrganizationId}
                  loading={requestingOrganizationId === org.id}
                  onClick={() => handleJoinRequest(org.id)}
                >
                  {requestingOrganizationId === org.id ? 'Requesting...' : 'Request to Join'}
                </Button>
              </div>
            </For>
          </div>

          <div className='text-muted-foreground'>
            Alternatively, you can <a onClick={switchToCreate} className='text-foreground underline hover:cursor-pointer'>create your own workspace</a>.
          </div>
        </When>
        <When condition={requested}>
          <h1 className='text-2xl font-extrabold tracking-tight my-1'>
            Hang tight! Your admin has been notified.
          </h1>
          <p className='my-2'>
            Once they approve your request, you'll receive an invitation email.
          </p>
          <p className='my-2'>
            Alternatively, go tap them on the shoulder :)
          </p>
          <div className='flex gap-2 mt-6'>
            <Button variant='outline' onClick={signOut}>
              Sign out
            </Button>
            <Button variant='outline' onClick={switchToCreate}>
              Create personal workspace
            </Button>
          </div>
        </When>
      </Choose>
    </BasicLayout>
  )
}

export default JoinOrganization
